import { OpenInNew } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  defaultFighterInfo,
  FighterInfo,
} from '../../../../../corelogic/domain/models/fighterInfo.model';
import { Gender } from '../../../../../corelogic/domain/models/gender.enum';
import { retrieveMostRecentFightResults } from '../../../../../corelogic/domain/usecases/fights/retrieveMostRecentFightResults.usecase';
import { checkSelectedSeasonFights } from '../../../../../corelogic/domain/utils/checkSelectedSeasonFights.utils';
import {
  buildFighterKey,
  getDefaultFighterImage,
} from '../../../../../corelogic/domain/utils/fighterImage.utils';
import { createFighterInfo } from '../../../../../corelogic/domain/utils/fighterInfo.utils';
import { isSeasonActiveById } from '../../../../../corelogic/domain/utils/seasons.utils';
import { calculateTotalPoints } from '../../../../../corelogic/domain/utils/selector.utils';
import { useAllFighterImagesState } from '../../hooks/useFighterImage';
import { useFighterState } from '../../hooks/useFighterState';
import { useFightState } from '../../hooks/useFightState';
import { useSeasonFightsState } from '../../hooks/useSeasonFightsState';
import { useSeasonState } from '../../hooks/useSeasonState';
import { useUserSeasonState } from '../../hooks/useUserSeasonState';
import { FighterImageService } from '../../services/FighterImage.service';
import { SeasonFightsService } from '../../services/SeasonFightsService';
import { UserSeasonService } from '../../services/UserSeasonService';
import SeasonSelector from '../SeasonSelector/SeasonSelector';
import ReusableAlert from '../Shared/ReusableAlert/ReusableAlert';
import FighterDetails from './FighterDetails/FighterDetails';
import SeasonInfoViewer from './SeasonInfoViewer/SeasonInfoViewer';
import ViewerCard from './ViewerCard/ViewerCard';

const TeamViewer: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Calculate dynamic spacing and padding based on screen size
  const { gridSpacing, gridPadding } = useMemo(
    () => ({
      gridSpacing: isMobile ? 0.75 : 2,
      gridPadding: isMobile ? 2 : 8,
    }),
    [isMobile],
  );

  // State hooks for current data
  const { fighters } = useFighterState();
  const { fights: allFights, isLoading: isFightsLoading } = useFightState();
  const { seasons } = useSeasonState();
  const {
    seasonId: userSeasonId,
    fighters: userSeasonFighters,
    isLoading: isUserSeasonLoading,
  } = useUserSeasonState();
  const {
    seasonId: seasonFightsId,
    fights: seasonFights,
    isLoading: isSeasonFightsLoading,
  } = useSeasonFightsState();

  // Use seasonFightsId as the "primary" ID for operations
  const selectedSeasonId = seasonFightsId;

  /* Other state variables */
  const [selectedFighter, setSelectedFighter] = useState<FighterInfo | null>(
    null,
  );
  const [snackbarText, setSnackbarText] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Memoize the fighters array to prevent infinite loop in useAllFighterImagesState
  const memoizedFighters = useMemo(() => {
    return userSeasonFighters
      .map((f) => fighters.find((ff) => ff.id === f.fighterId))
      .filter(Boolean);
  }, [userSeasonFighters, fighters]);

  const {
    fighterImages,
    isLoading: isImagesLoading,
    loadingStates,
  } = useAllFighterImagesState(memoizedFighters);

  // Helper function to check if a specific fighter image is loading
  const isFighterImageLoading = (fighterInfo: FighterInfo): boolean => {
    if (fighterInfo.FighterName === 'No Fighter Selected') return false;

    // Find the fighter to get the key
    const fighter = fighters.find((f) => f.id === fighterInfo.FighterId);
    if (!fighter) return false;

    const fighterKey = buildFighterKey(
      fighter.firstname,
      fighter.lastname,
      fighter.dob,
    );
    // Use individual loading state instead of global
    return fighterKey ? loadingStates[fighterKey] || false : false;
  };

  /* Verify seasonIds match */
  useEffect(() => {
    if (userSeasonId && seasonFightsId && userSeasonId !== seasonFightsId) {
      console.warn('Season ID mismatch between user season and season fights');
    }

    if (
      userSeasonId &&
      seasonFightsId &&
      userSeasonId !== seasonFightsId &&
      !isSeasonFightsLoading &&
      seasonFights.length > 0
    ) {
      UserSeasonService.retrieveUserSeason(seasonFightsId);
    }
  }, [userSeasonId, seasonFightsId, isSeasonFightsLoading, seasonFights]);

  // Separate effect to call service for missing images - prevents infinite loop
  useEffect(() => {
    // Small delay to let hook state settle after navigation
    const timeoutId = setTimeout(() => {
      userSeasonFighters.forEach((userFighter) => {
        const fighterDetails = fighters.find(
          (f) => f.id === userFighter.fighterId,
        );

        if (fighterDetails) {
          FighterImageService.retrieveFighterImage(
            fighterDetails.firstname,
            fighterDetails.lastname,
            fighterDetails.dob,
            fighterDetails.gender,
          );
        }
      });
    }, 100); // Small delay to let hook state settle

    return () => clearTimeout(timeoutId);
  }, [userSeasonFighters, fighters]);

  // Handler for season changes
  const handleSeasonChange = (seasonId: string) => {
    // Don't show error if fights are still loading
    if (isFightsLoading) {
      console.log('Fights still loading, proceeding with season change...');
      SeasonFightsService.retrieveSeasonFights(seasonId);
      UserSeasonService.retrieveUserSeason(seasonId);
      return;
    }

    const fightsInSeason = checkSelectedSeasonFights(
      seasons,
      allFights,
      seasonId,
    );

    if (!fightsInSeason) {
      setSnackbarText(
        `No Events scheduled for this season. Please select another season.`,
      );
      setOpenSnackbar(true);
      return;
    }

    SeasonFightsService.retrieveSeasonFights(seasonId);
    UserSeasonService.retrieveUserSeason(seasonId);
  };

  /* Fighter Info array stores the info for your currently selected fighters */
  const fighterInfoArray = useMemo(() => {
    // Initialize array with default values
    const fighterCards = Array(8)
      .fill(null)
      .map((_, index) => ({
        ...defaultFighterInfo(),
        CardId: index + 1,
      }));

    userSeasonFighters.forEach((userFighter) => {
      // Skip if no position or invalid position
      if (
        !userFighter.position ||
        userFighter.position < 1 ||
        userFighter.position > 8
      ) {
        console.error(
          `Fighter ${userFighter.fighterId} has invalid position: ${userFighter.position}`,
        );
        return;
      }

      /* Find the fighter details */
      const fighterDetails = fighters.find(
        (f) => f.id === userFighter.fighterId,
      );

      if (!fighterDetails) {
        console.error(
          `Fighter not found for position ${userFighter.position}:`,
          userFighter,
        );
        return;
      }

      /* Find the fight for the fighter */
      const fight = seasonFights.find(
        (f) => f.fight_id === userFighter.fightId,
      );
      if (!fight) {
        console.error(
          `Fight not found for position ${userFighter.position}:`,
          userFighter,
        );
        return;
      }

      const arrayIndex = userFighter.position - 1;
      const fighterKey = buildFighterKey(
        fighterDetails.firstname,
        fighterDetails.lastname,
        fighterDetails.dob,
      );

      // Get image from state or use default - service called in useEffect above
      const fighterImage =
        (fighterKey && fighterImages[fighterKey]) ||
        getDefaultFighterImage(fighterDetails.gender);

      const result = createFighterInfo(
        userFighter.position,
        fighterDetails,
        fighterImage,
        fight,
        userFighter.points || null,
      );

      // Check if the result is an error string
      if (typeof result === 'string') {
        console.error(
          `Error creating fighter info for position ${userFighter.position}:`,
          result,
        );
        return;
      }

      // If we get here, result is a valid FighterInfo object
      const fighterInfo = result;
      fighterCards[arrayIndex] = fighterInfo;
    });

    return fighterCards;
  }, [userSeasonFighters, fighters, seasonFights, fighterImages]);

  const handleFighterClick = (fighterInfo: FighterInfo) => {
    // only set if fight is not null
    if (fighterInfo.FightId) {
      setSelectedFighter(fighterInfo);
    }
  };

  /* Find the fighter details */
  const findFighter = (fighterId: string) => {
    if (!fighterId) return null;
    return fighters.find((f) => f.id === fighterId);
  };

  // Calculate total points from the fighterInfoArray
  const totalPoints = useMemo(() => {
    return calculateTotalPoints(fighterInfoArray);
  }, [fighterInfoArray]);

  // Calculate fights remaining (fighters with 0 points)
  const fightsRemaining = useMemo(() => {
    return fighterInfoArray.filter(
      (fighter) =>
        fighter.FighterName !== 'No Fighter Selected' &&
        (fighter.Points === null || fighter.Points === 0),
    ).length;
  }, [fighterInfoArray]);

  // Find highest scoring fighter
  const highestScoringFighter = useMemo(() => {
    const fightersWithPoints = fighterInfoArray.filter(
      (fighter) =>
        fighter.FighterName !== 'No Fighter Selected' &&
        fighter.Points !== null &&
        fighter.Points > 0,
    );

    if (fightersWithPoints.length === 0) {
      return null;
    }

    return fightersWithPoints.reduce((highest, current) =>
      (current.Points || 0) > (highest.Points || 0) ? current : highest,
    );
  }, [fighterInfoArray]);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  // Check if current season is active
  const isCurrentSeasonActive = useMemo(() => {
    return isSeasonActiveById(selectedSeasonId, seasons);
  }, [selectedSeasonId, seasons]);

  // Style objects for central stats
  const statLabelStyle = {
    color: '#ffffff',
    fontFamily: 'Rubik Mono One',
    fontSize: {
      xs: '0.45rem',
      sm: 'clamp(0.45rem, 0.7vw, 1.0rem)',
    },
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundImage:
          'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(/viewer_octagon.png)',
        backgroundSize: { xs: '200%', md: 'cover' },
        backgroundPosition: { xs: 'center 12px', md: 'center' },
        backgroundRepeat: 'no-repeat',
        overflow: 'hidden',
      }}
    >
      {/* Loading Bar */}
      {(isUserSeasonLoading || isSeasonFightsLoading) && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(255,255,255,0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#cc0000',
            },
          }}
        />
      )}

      {/* Content Box - now takes full height */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
        }}
      >
        {/* Left Box - 20% */}
        <Box
          component={motion.div}
          variants={container}
          initial="hidden"
          animate="show"
          sx={{
            width: { xs: '90%', md: '20%' },
            height: { xs: 'auto', md: '100%' },
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            paddingLeft: { xs: 0, md: '10px' },
            paddingTop: '10px',
            paddingBottom: { xs: '20px', md: 0 },
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            order: { xs: 3, md: 1 },
            mx: { xs: 'auto', md: 0 },
          }}
        >
          <SeasonInfoViewer
            seasonFights={seasonFights}
            fighterInfoArray={fighterInfoArray}
          />
        </Box>

        {/* Middle Box - 60% */}
        <Box
          sx={{
            width: { xs: '100%', md: '60%' },
            height: { xs: 'auto', md: '100%' },
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: '5px',
            paddingBottom: { xs: '20px', md: 0 },
            overflow: 'hidden',
            order: { xs: 1, md: 2 },
            '& .MuiGrid-container': {
              minHeight: 0,
            },
          }}
        >
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            style={{
              width: '100%',
              height: 'auto',
              maxWidth: 'min(100%, calc(100vh - 70px))',
              aspectRatio: '1 / 1',
              position: 'relative',
              margin: 0,
            }}
          >
            <Grid
              container
              spacing={gridSpacing}
              sx={{
                width: '100%',
                height: 'auto',
                maxWidth: { xs: '100%', sm: 'min(100%, calc(100vh - 70px))' },
                aspectRatio: '1 / 1',
                position: 'relative',
                margin: 0,
                '& .MuiGrid-item': {
                  paddingTop: `${gridPadding}px`,
                  paddingLeft: `${gridPadding}px`,
                  paddingBottom: `${gridPadding}px`,
                  paddingRight: `${gridPadding}px`,
                },
              }}
            >
              {/* Central merged box */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: '40%', sm: '50%' },
                  height: { xs: '40%', sm: '50%' },
                  zIndex: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: { xs: '2px', sm: '10px' },
                }}
              >
                {/* Centered Content */}
                <Box sx={{ textAlign: 'center' }}>
                  {/* Total Points Section with Animated Background */}
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-block',
                      padding: { xs: '8px 16px', sm: '12px 20px' },
                      borderRadius: '12px',
                      background: 'rgba(192, 192, 192, 0.15)',
                      backdropFilter: 'blur(4px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      marginBottom: '20px',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background:
                          'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                        animation: 'sheen 4s infinite',
                      },
                      '@keyframes sheen': {
                        '0%': { left: '-100%' },
                        '100%': { left: '100%' },
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#ffffff',
                        fontFamily: 'Rubik Mono One',
                        fontSize: {
                          xs: '0.55rem',
                          sm: 'clamp(0.4rem, 1.0vw, 1.8rem)',
                        },
                        textAlign: 'center',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        marginBottom: '4px',
                      }}
                    >
                      Total Points:
                    </Typography>
                    <Typography
                      sx={{
                        color: '#cc0000',
                        fontFamily: 'Rubik Mono One',
                        fontSize: {
                          xs: '0.65rem',
                          sm: 'clamp(0.6rem, 1.2vw, 2rem)',
                        },
                        textAlign: 'center',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                      }}
                    >
                      {totalPoints}
                    </Typography>
                  </Box>

                  {/* Additional Stats - Separate Lines */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: { xs: '2px', sm: '6px' },
                      textAlign: 'center',
                    }}
                  >
                    <Typography sx={statLabelStyle}>
                      Fights Remaining:{' '}
                      <span style={{ color: '#cc0000' }}>
                        {fightsRemaining}
                      </span>
                    </Typography>
                    <Typography sx={statLabelStyle}>
                      Top Scorer:{' '}
                      <span style={{ color: '#cc0000' }}>
                        {highestScoringFighter
                          ? `${highestScoringFighter.FighterName} (${highestScoringFighter.Points})`
                          : 'N/A'}
                      </span>
                    </Typography>
                  </Box>

                  {/* Edit Team Button - Only show for active seasons */}
                  {isCurrentSeasonActive && (
                    <Button
                      variant="contained"
                      size="small"
                      endIcon={<OpenInNew />}
                      onClick={() => navigate('/teamselector')}
                      sx={{
                        mt: 2,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(204, 0, 0, 0.3)',
                        color: 'white',
                        fontSize: {
                          xs: '0.35rem',
                          sm: 'clamp(0.3rem, 0.6vw, 0.8rem)',
                        },
                        fontFamily: 'Rubik Mono One',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(204, 0, 0, 0.5)',
                        },
                      }}
                    >
                      Edit Team
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Grid items */}
              {Array.from({ length: 16 }).map((_, index) => {
                const row = Math.floor(index / 4);
                const col = index % 4;

                const isHighlighted =
                  (row === 0 && (col === 1 || col === 2)) ||
                  (row === 3 && (col === 1 || col === 2)) ||
                  ((row === 1 || row === 2) && (col === 0 || col === 3));

                const isMiddleCell =
                  (row === 1 || row === 2) && (col === 1 || col === 2);

                const isCornerCell =
                  (row === 0 && (col === 0 || col === 3)) ||
                  (row === 3 && (col === 0 || col === 3));

                // Add season selector to top right corner cell
                if (row === 0 && col === 3) {
                  return (
                    <Grid
                      item
                      xs={3}
                      key={index}
                      sx={{
                        height: '25%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-end',
                        padding: { xs: '2px', sm: '8px' },
                      }}
                    >
                      <SeasonSelector
                        selectedSeasonId={selectedSeasonId}
                        onSeasonChange={handleSeasonChange}
                      />
                    </Grid>
                  );
                }

                // Calculate the fighterInfo array index based on grid position
                const fighterIndex = (() => {
                  if (isMiddleCell || isCornerCell) return -1;
                  let count = 0;
                  for (let i = 0; i < index; i++) {
                    const r = Math.floor(i / 4);
                    const c = i % 4;
                    if (
                      !((r === 1 || r === 2) && (c === 1 || c === 2)) && // not middle
                      !(
                        (r === 0 && (c === 0 || c === 3)) ||
                        (r === 3 && (c === 0 || c === 3))
                      )
                    ) {
                      // not corner
                      count++;
                    }
                  }
                  return count;
                })();

                return (
                  <Grid
                    item
                    xs={3}
                    key={index}
                    sx={{
                      height: '25%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {!isMiddleCell &&
                      !isCornerCell &&
                      fighterIndex >= 0 &&
                      fighterIndex < fighterInfoArray.length && (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '4px',
                          }}
                        >
                          <ViewerCard
                            image={fighterInfoArray[fighterIndex].FighterImage}
                            name={fighterInfoArray[fighterIndex].FighterName}
                            opponent={fighterInfoArray[fighterIndex].Opponent}
                            fightDate={fighterInfoArray[fighterIndex].FightDate}
                            points={fighterInfoArray[fighterIndex].Points}
                            isHighlighted={isHighlighted}
                            isSelected={
                              selectedFighter?.CardId ===
                              fighterInfoArray[fighterIndex].CardId
                            }
                            hasSelectedFighter={selectedFighter !== null}
                            title={fighterInfoArray[fighterIndex].Title}
                            gender={
                              fighterInfoArray[fighterIndex].Gender as Gender
                            }
                            onClick={() =>
                              handleFighterClick(fighterInfoArray[fighterIndex])
                            }
                            isLoading={isFighterImageLoading(
                              fighterInfoArray[fighterIndex],
                            )}
                          />
                        </Box>
                      )}
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>
        </Box>

        {/* Right Box - 20% */}
        <Box
          sx={{
            width: { xs: '90%', md: '20%' },
            height: { xs: 'auto', md: '100%' },
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            paddingRight: { xs: 0, md: '10px' },
            paddingTop: '10px',
            paddingBottom: { xs: '20px', md: 0 },
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            order: { xs: 2, md: 3 },
            mx: { xs: 'auto', md: 0 },
          }}
        >
          {selectedFighter ? (
            <FighterDetails
              open={selectedFighter !== null}
              onClose={() => setSelectedFighter(null)}
              fighterSeasonInfo={selectedFighter}
              fighter={findFighter(selectedFighter?.FighterId || '') ?? null}
              seasonFight={
                seasonFights.find(
                  (seasonFight) =>
                    seasonFight.fight_id === selectedFighter?.FightId,
                ) ?? null
              }
              fight={
                allFights.find(
                  (fight) => fight.id === selectedFighter?.FightId,
                ) ?? null
              }
              recentFightResults={retrieveMostRecentFightResults(
                allFights,
                selectedFighter?.FighterId || '',
                5,
              )}
            />
          ) : (
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              sx={{
                bgcolor: 'rgba(50, 50, 50, 0.9)',
                padding: '10px',
                borderRadius: '8px',
                textAlign: 'center',
                width: '80%',
              }}
            >
              <Typography
                sx={{
                  color: '#cccccc',
                  fontFamily: 'Rubik Mono One',
                  fontSize: 'clamp(0.4rem, 0.8vw, 1rem)',
                }}
              >
                Select a fighter to view their details
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <ReusableAlert
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        severity="warning"
        text={snackbarText}
      />
    </Box>
  );
};

export default TeamViewer;
